import { api } from "../config/variables";

import { get } from "./barrels/lodash";
import {
	getValidCookie,
	goToLoginPage,
	reloadUser,
	storeAbdRedirectIntent,
} from "./functions";

/**
 * Session-expiry recovery, in one place.
 *
 * Every thunk in this app used to inline the same three lines on a 401:
 *
 *     reloadUser();                       // erase bearer / expires / client
 *     dispatch(setUser({ reAuth: false, showReAuthModal: false, requestType }));
 *     window.location.reload();
 *
 * That only ever worked because of something the thunk could not see. The
 * reload lands back in `PrivateRoute`, which finds no bearer cookie and does
 * the part that actually rescues the user (Authorization.jsx):
 *
 *     {bearerToken || isSellerProfileRoute
 *         ? renderPrivateContent()
 *         : Boolean(authUrl) && window.location.replace(authUrl)}
 *
 * So the recovery is two halves, and the thunk only owns the first. On any
 * surface mounted OUTSIDE `PrivateRoute` the second half never runs, and the
 * first half on its own is actively harmful: it deletes the session and then
 * reloads the user onto the same screen with no way back in.
 *
 * `/depot-connect-v2` — the Cargo m-browser — is exactly that surface. It is a
 * plain `<Route>` in index.jsx, a sibling of the `<PrivateRoute>` blocks rather
 * than one of them. A token that expired mid-flow left the user on a dead
 * screen where every save failed and the raw API envelope was rendered into a
 * snackbar, with no route to login. That is the bug this module closes.
 *
 * The fix is to make the recovery self-contained instead of relying on a guard
 * that may or may not be above you: erase the session and go to login in one
 * step. `PrivateRoute` is left exactly as it is — this reaches the same
 * destination it would have, just without depending on it.
 */

/**
 * Rejection value a thunk uses once it has handed the session off to
 * `handleAuthExpiry`. Callers match on it to stay silent, the same way
 * `REQUEST_CANCELLED_ERROR` marks a rejection that must not reach a snackbar:
 * the redirect is already in flight, and an error toast on the way out is
 * exactly the raw-envelope noise this change removes.
 */
export const AUTH_EXPIRED_ERROR = "auth_session_expired_error";

/** Set once we have committed to navigating away, so concurrent 401s from
 *  parallel requests don't each fire their own redirect. */
let redirecting = false;

/** DRF's message for a missing/expired credential, which is what the API
 *  replies with once the bearer cookie is gone. */
const AUTH_DETAIL_PATTERN = /authentication credentials were not provided/i;

/**
 * Reads the auth-failure detail string out of either shape this backend uses:
 * plain DRF (`{ detail }`) or the gateway envelope (`{ errors: { detail } }`).
 */
const readAuthDetail = (err: unknown): string | null => {
	const detail =
		get(err, "response.data.errors.detail") ?? get(err, "response.data.detail");
	return typeof detail === "string" ? detail : null;
};

/**
 * True when a rejection means "the session is gone", rather than "the request
 * was invalid".
 *
 * Status alone is not enough. This backend is already known to forward an
 * upstream 401 inside a body carrying a different status — see
 * `readVerbatimApiError` in sanitizeError.ts, added because the wallet API
 * replies `{ "error": "('Could not decode token: ...', 401, '<url>')" }`. A
 * status-only check silently misses that and the user gets the raw envelope
 * instead of a redirect, which is the failure mode seen on the Cargo save.
 *
 * Deliberately narrow: it matches an explicit 401, or an auth-credentials
 * detail string. A validation rejection, a duplicate-name 400 or a network
 * error must NOT match — those carry a message the user can act on, and
 * treating one as an expiry would throw away their filled-in form.
 *
 * @param err - Caught axios error, or the value a thunk rejected with.
 * @returns Whether the session should be considered expired.
 */
export const isAuthExpired = (err: unknown): boolean => {
	if (get(err, "response.status") === 401) {
		return true;
	}
	const detail = readAuthDetail(err);
	if (detail && AUTH_DETAIL_PATTERN.test(detail)) {
		return true;
	}
	// The wallet-style envelope: a stringified upstream tuple with the status
	// embedded. Matched on the token wording as well as the code so an
	// unrelated body that happens to contain "401" doesn't trigger a logout.
	const verbatim = get(err, "response.data.error");
	return (
		typeof verbatim === "string" &&
		/\b401\b/.test(verbatim) &&
		/token|credential|auth/i.test(verbatim)
	);
};

/**
 * True when this browser holds no usable bearer cookie — i.e. the user is not
 * logged in at all, as opposed to logged in and rejected.
 *
 * Needed because the guard-less Cargo route produces a SECOND, quieter failure
 * that never reaches an API call. `useClientStats` only dispatches
 * `fetchCurrentUser` when a bearer cookie exists, so with no cookie
 * `users.currentUser` stays null, `selectCurrentUserClientId` stays undefined,
 * and every save throws "Could not identify your account. Please refresh and
 * try again." — advice that cannot work, because the refresh re-runs the same
 * cookie-less mount and fails identically.
 *
 * That state is reachable two ways, both traced to the same missing guard:
 * a prior 401 erased the cookie, or the user was deep-linked in with a
 * `?token=` param — which ONLY `PrivateRoute.handleIncompleteProfile` converts
 * into a cookie, and this route is not under it.
 */
export const isUnauthenticated = (): boolean => !getValidCookie("bearer");

/**
 * Ends the dead session and sends the user to login.
 *
 * Same two effects `PrivateRoute` produces for an expired desktop session —
 * cookies cleared, `window.location.replace` to the SSO login URL — performed
 * directly so it also works on routes that have no guard above them.
 *
 * `replace`, not `assign`: the dead page must not stay in history, or Back
 * returns the user to the screen they were just rescued from.
 *
 * Callers should stop their own work after calling this (return / bail out).
 * Navigation is not instant — the current tick keeps running — so anything
 * after this call still executes, and showing an error snackbar there is what
 * put the raw envelope in front of the user in the first place.
 */
export const handleAuthExpiry = (): void => {
	if (redirecting) {
		return;
	}
	redirecting = true;

	// Erase bearer / expires / client. Unchanged from the previous recovery —
	// leaving a stale bearer behind would let the next request 401 again.
	reloadUser();
	// Preserves the ABD auto-redirect intent across the login round trip, the
	// same call PrivateRoute makes before building the login URL.
	storeAbdRedirectIntent();

	const { url } = goToLoginPage();
	if (!url || !api.sso_url) {
		// No login URL to go to. A reload is still better than sitting on a
		// screen whose every request now fails.
		redirecting = false;
		window.location.reload();
		return;
	}
	window.location.replace(url);
};

/**
 * The one call sites should use: redirects to login and returns true when this
 * rejection means the session is gone, false when it is an ordinary error the
 * caller should report as usual.
 *
 * Recognises all three forms an expiry reaches a catch block in:
 *   - `AUTH_EXPIRED_ERROR` as a bare rejection value (thunk `rejectWithValue`)
 *   - the same string as an `Error.message` (thrown by a pre-request guard)
 *   - a raw API rejection that `isAuthExpired` matches (401 / auth envelope)
 *
 * Callers MUST return immediately when this returns true. Navigation does not
 * halt the current tick, so anything after it still runs — and a toast painted
 * on the way out is the raw-envelope noise this whole change removes.
 */
export const redirectIfAuthExpired = (err: unknown): boolean => {
	const isSentinel =
		err === AUTH_EXPIRED_ERROR ||
		(err instanceof Error && err.message === AUTH_EXPIRED_ERROR);
	if (!isSentinel && !isAuthExpired(err)) {
		return false;
	}
	handleAuthExpiry();
	return true;
};

/** Test seam: clears the one-shot guard between cases. */
export const resetAuthExpiryGuard = (): void => {
	redirecting = false;
};
